import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { adminAPI, authAPI } from '../services/api';
import ExtensionPromptModal from '../components/admin/ExtensionPromptModal';
import QueueRolePromptModal from '../components/admin/QueueRolePromptModal';
import { setManagementReportAccessFromLogin } from '../utils/managementReportAccess';
import { setComplaintsAccessFromLogin } from '../utils/complaintsAccess';
import { setMonitoringAccessFromLogin } from '../utils/monitoringAccess';
import { setAmiAccessFromLogin } from '../utils/amiAccess';
import { setCallCenterReportAccessFromLogin } from '../utils/callCenterReportAccess';
import { setHisReportAccessFromLogin } from '../utils/hisReportAccess';
import { setCustomerClubAccessFromLogin, setCustomerClubSegmentsAccessFromLogin } from '../utils/customerClubAccess';
import {
  setScreenPopAccessFromLogin,
  setStoredPhoneExtension,
  setExtensionConfirmedThisSession,
} from '../utils/screenPopAccess';
import { clearQueueLoginRole, setQueueLoginRole } from '../utils/queueLoginRole';

interface LoginFormData {
  username: string;
  password: string;
}

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [askExtension, setAskExtension] = useState(false);
  const [askQueueRole, setAskQueueRole] = useState(false);
  const [checkingQueue, setCheckingQueue] = useState(false);
  const [savedExtension, setSavedExtension] = useState('');

  const primaryButtonClass = 'inline-flex items-center justify-center rounded-lg bg-gray-900 text-white py-3 px-4 text-sm font-medium transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-70 disabled:cursor-not-allowed';
  const secondaryButtonClass = 'inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-800 py-3 px-4 text-sm font-medium transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300';

  const goDashboard = () => {
    navigate('/admin/dashboard');
  };

  /** بعد از داخلی: اگر سیستم صف فعال است سؤال نقش را قبل از داشبورد بپرس */
  const afterExtensionConfirmed = async () => {
    setAskExtension(false);
    setCheckingQueue(true);
    try {
      const { data } = await adminAPI.getQueueLoginConfig();
      if (data.feature_enabled) {
        setAskQueueRole(true);
        return;
      }
      // فیچر خاموش → نقش لازم نیست
      setQueueLoginRole('outside');
      goDashboard();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      // خطای auth واقعی را پنهان نکن
      if (status === 401 || status === 403) {
        setError(
          typeof detail === 'string'
            ? detail
            : 'نشست ورود نامعتبر است. دوباره وارد شوید.'
        );
        setAskExtension(true);
        return;
      }
      // کانفیگ صف در دسترس نبود — بدون نقش وارد شو
      goDashboard();
    } finally {
      setCheckingQueue(false);
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authAPI.login(data.username, data.password);
      const payload = response.data as {
        access_token: string;
        role?: string;
        management_report_access?: boolean;
        complaints_access?: boolean;
        monitoring_access?: boolean;
        ami_access?: boolean;
        call_center_report_access?: boolean;
        his_report_access?: boolean;
        screen_pop_access?: boolean;
        customer_club_access?: boolean;
        customer_club_segments_access?: boolean;
        phone_extension?: string | null;
      };

      localStorage.setItem('admin_token', payload.access_token);
      localStorage.setItem('admin_username', data.username);
      localStorage.setItem('admin_role', payload.role || 'user');
      setManagementReportAccessFromLogin(payload.management_report_access);
      setComplaintsAccessFromLogin(payload.complaints_access);
      setMonitoringAccessFromLogin(payload.monitoring_access);
      setAmiAccessFromLogin(payload.ami_access);
      setCallCenterReportAccessFromLogin(payload.call_center_report_access);
      setHisReportAccessFromLogin(payload.his_report_access);
      setCustomerClubAccessFromLogin(payload.customer_club_access);
      setCustomerClubSegmentsAccessFromLogin(payload.customer_club_segments_access);

      const screenPopAccess = Boolean(payload.screen_pop_access);
      setScreenPopAccessFromLogin(screenPopAccess);
      setExtensionConfirmedThisSession(false);
      clearQueueLoginRole();

      const savedExt = (payload.phone_extension || '').trim();
      if (savedExt) {
        setStoredPhoneExtension(savedExt);
        setSavedExtension(savedExt);
      }

      if (screenPopAccess) {
        // قبل از ورود به داشبورد، داخلی و در صورت نیاز نقش صف را بپرس
        setAskExtension(true);
        return;
      }

      goDashboard();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'خطا در ورود');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            ورود به پنل مدیریت
          </h2>
          <p className="text-gray-600">
            مرکز تصویربرداری پزشکی
          </p>
        </div>

        <div className="bg-white shadow-xl rounded-2xl p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-800 text-sm font-medium">{error}</span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                نام کاربری
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  type="text"
                  {...register('username', { required: 'نام کاربری الزامی است' })}
                  className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="نام کاربری"
                />
              </div>
              {errors.username && (
                <p className="text-red-500 text-sm">{errors.username.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                رمز عبور
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  type="password"
                  {...register('password', { required: 'رمز عبور الزامی است' })}
                  className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="رمز عبور"
                />
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm">{errors.password.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`${primaryButtonClass} w-full`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  در حال ورود...
                </div>
              ) : (
                'ورود'
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                className={secondaryButtonClass}
              >
                بازگشت به صفحه اصلی
              </button>
            </div>
          </form>
        </div>
      </div>

      {askExtension && (
        <ExtensionPromptModal
          initialExtension={savedExtension}
          onConfirmed={() => {
            void afterExtensionConfirmed();
          }}
        />
      )}

      {checkingQueue && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-4">
          <div className="rounded-xl bg-white px-6 py-4 text-sm text-gray-700 shadow-xl">
            در حال آماده‌سازی ورود...
          </div>
        </div>
      )}

      {askQueueRole && (
        <QueueRolePromptModal
          onConfirmed={() => {
            setAskQueueRole(false);
            goDashboard();
          }}
        />
      )}
    </div>
  );
};

export default AdminLogin;
